import { useEffect, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';

import { API_V3, ApiV3Response } from '@dbt-labs/app-nav';

export interface RecentlyViewedParams {
  accountId?: number;
  name?: string;
  description?: string;
  url?: string;
  resourceType?: string;
}

export interface RecentlyViewedRecord {
  name: string;
  description: string;
  url: string;
  resourceType: string;
}

export interface RecentlyViewedAPIRecord {
  name: string;
  description: string;
  url: string;
  resource_type: string;
}

const postRecentlyViewed = async (
  record: RecentlyViewedParams,
): Promise<RecentlyViewedRecord> => {
  const response = await axios.post<RecentlyViewedRecord>(
    `${API_V3}/accounts/${record.accountId}/recently-viewed/`,
    {
      name: record.name,
      description: record.description,
      resource_type: record.resourceType,
      url: record.url,
    },
  );

  return response.data;
};

export const useTrackRecentlyViewed = (
  params: RecentlyViewedParams,
  /** When this condition is true, the mutation will not be called. Should be used to prevent tracking before the resource is loaded. */
  pause: boolean,
) => {
  const trackedUrl = useRef('');
  const { mutateAsync } = useMutation({
    mutationFn: postRecentlyViewed,
  });

  useEffect(() => {
    const { accountId, url, name, description = '', resourceType } = params;
    const urlChanged = trackedUrl.current === '' || trackedUrl.current !== url;

    if (!pause && urlChanged && accountId && url) {
      mutateAsync({
        accountId,
        url,
        name: name ?? '',
        description,
        resourceType: resourceType?.toLowerCase() ?? 'unknown',
      });
      trackedUrl.current = url;
    }
  }, [params, mutateAsync, pause]);
};

export const getRecentlyViewed = async (
  accountId: number,
): Promise<RecentlyViewedRecord[]> => {
  const response = await axios.get<ApiV3Response<RecentlyViewedAPIRecord[]>>(
    `${API_V3}/accounts/${accountId}/recently-viewed/`,
  );
  return (
    response?.data?.data.map((r) => {
      return {
        name: r.name,
        description: r.description,
        url: r.url,
        resourceType: r.resource_type,
      };
    }) ?? []
  );
};

export const useRecentlyViewed = (accountId: number) => {
  const enabled = !!accountId;
  return useQuery<RecentlyViewedRecord[], AxiosError>({
    queryKey: ['account-recently-viewed', { accountId }],
    queryFn: () => getRecentlyViewed(accountId ?? 0),
    enabled,
  });
};
